/**
 * Atlas Canonical Data Contract
 *
 * Single source of truth for how each UI question (identified by questionId)
 * maps to the engine's SystemRecInput. This contract sits between the UI
 * registry and the engine: the UI normalises raw answers → canonical keys,
 * and the adapter (toEngineInput) maps canonical keys → engine input.
 *
 * Key concepts:
 *   - `engineKey`     Engine input key when it differs from the canonical key.
 *   - `engineEnumMap` Per-value translation when UI enum values differ from engine enums.
 *   - `uiOnly`        Field is intentionally not sent to the engine.
 *
 * Bump ATLAS_DATA_CONTRACT_VERSION when fields are added/removed/renamed.
 */

import { ATLAS_DATA_CONTRACT_VERSION as _ATLAS } from '../contracts/dataContract';

export { ATLAS_DATA_CONTRACT_VERSION } from '../contracts/dataContract';

// ---------------------------------------------------------------------------
// FieldDef type
// ---------------------------------------------------------------------------

export interface FieldDef {
  /** Canonical key — matches the questionId in the mapping registry. */
  key: string;

  /** Data type expected for the canonical value. */
  type: 'string' | 'number' | 'boolean' | 'enum' | 'array' | 'object';

  /** Key into the `enums` record for the canonical allowed values. */
  enumRef?: string;

  /** Whether the engine requires this field for a valid recommendation. */
  required?: boolean;

  /**
   * If true this field is intentionally UI-only and the adapter will not send
   * it to the engine. No mismatch is expected.
   */
  uiOnly?: boolean;

  /**
   * Engine input key when the engine uses a different name from the canonical
   * questionId. Dot-notation is supported (e.g. "emitters.type").
   */
  engineKey?: string;

  /**
   * Per-value translation from canonical enum value → engine enum value.
   * Only needed when the two sides use different strings for the same concept.
   * @example { good: 'high', average: 'medium', poor: 'poor', unknown: 'unknown' }
   */
  engineEnumMap?: Record<string, string>;

  /** Human-readable note explaining the mapping decision. */
  notes?: string;
}

// ---------------------------------------------------------------------------
// Shared enum value arrays (canonical side)
// ---------------------------------------------------------------------------

export const enums = {
  // Property
  propertyType: ['flat', 'maisonette', 'terraced', 'end_terrace', 'semi', 'detached', 'bungalow', 'unknown'] as const,
  propertyAgeBand: ['pre_1919', '1919_1944', '1945_1964', '1965_1980', '1981_2000', '2001_2010', '2011_plus', 'unknown'] as const,

  // Occupancy
  occupancyTiming: ['mornings_evenings', 'evenings_weekends', 'most_of_day', 'variable', 'unknown'] as const,
  temporaryGuests: ['none', 'occasional', 'frequent', 'permanent_addition'] as const,

  // Mains / DHW
  flowRate: ['8', '12', '18', '24', '30', 'unknown'] as const,
  coldMainType: ['mdpe', 'copper', 'lead', 'unknown'] as const,

  // Electrical (canonical UI values)
  electricalCapacity: ['unknown', 'standard', 'upgraded', 'three_phase', 'limited'] as const,
  // Engine-side enum values (used for engineCapabilities)
  electricalCapacityConfidence: ['unknown', 'insufficient', 'likely_ok'] as const,

  // Baseline / efficiency
  systemAge: ['0-5', '5-10', '10-15', '15-20', '20+', 'unknown'] as const,
  efficiencySource: ['sedbuk', 'epc', 'erp', 'unknown'] as const,
  sedbukBand: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown'] as const,
  epcBand: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'unknown'] as const,
  currentFuelType: ['gas', 'lpg', 'oil', 'electric'] as const,
  sludgeRisk: ['none', 'possible', 'likely', 'confirmed'] as const,

  // Emitters
  emitterType: ['radiators', 'underfloor', 'mixed', 'unknown'] as const,
  radiatorSize: ['small', 'typical', 'oversized', 'unknown'] as const,
  ufhExtent: ['none', 'partial', 'full'] as const,
  emitterRoomsStruggle: ['yes', 'no', 'unknown'] as const,
  pipeBore: ['8mm', '10mm', '15mm', '22mm', 'unknown'] as const,
  heatEmitterType: ['standard_rads', 'oversized_rads', 'underfloor_heating'] as const,

  // Insulation / retrofit
  insulationLevel: ['good', 'average', 'poor', 'unknown'] as const,
  retrofitLoft: ['none', 'some', 'good', 'unknown'] as const,
  retrofitWall: ['none', 'cavity', 'solid_or_internal_or_external', 'unknown'] as const,
  retrofitWindows: ['single', 'older_double', 'modern_double_or_triple', 'unknown'] as const,
  retrofitDraught: ['none', 'partial', 'modern', 'unknown'] as const,

  // Space
  availableSpace: ['airing_cupboard_loft', 'airing_cupboard', 'loft', 'garage', 'limited', 'none', 'unknown'] as const,
  spaceAvailability: ['small', 'medium', 'large'] as const,

  // Solar
  solarIntent: ['none', 'planned', 'installed', 'unknown'] as const,
} as const;

// ---------------------------------------------------------------------------
// FieldDef registry
// ---------------------------------------------------------------------------

export const fields: Record<string, FieldDef> = {

  // ── PROPERTY ──────────────────────────────────────────────────────────────
  propertyType: {
    key: 'propertyType',
    type: 'enum',
    enumRef: 'propertyType',
    required: true,
    // Same key as engine — just needed to register the enum so capabilities diff works.
  },

  propertyAgeBand: {
    key: 'propertyAgeBand',
    type: 'enum',
    enumRef: 'propertyAgeBand',
    required: true,
  },

  bedrooms: { key: 'bedrooms', type: 'number', required: true },
  bathrooms: { key: 'bathrooms', type: 'number', required: true },

  availableSpace: {
    key: 'availableSpace',
    type: 'enum',
    enumRef: 'availableSpace',
    engineKey: 'spaceAvailability',
    engineEnumMap: {
      airing_cupboard_loft: 'medium',
      airing_cupboard: 'medium',
      loft: 'medium',
      garage: 'medium',
      limited: 'small',
      none: 'small',
      unknown: 'medium',
    },
    notes: 'Engine collapses detailed space options to small | medium | large.',
  },

  outdoorSpace: {
    key: 'outdoorSpace',
    type: 'enum',
    uiOnly: true,
    notes: 'Used in UI eligibility hints; not a direct SystemRecInput field (engine infers from propertyType).',
  },

  // ── OCCUPANCY ────────────────────────────────────────────────────────────
  occupancyCount: {
    key: 'occupancyCount',
    type: 'number',
    required: true,
    engineKey: 'occupancy',
    notes: 'Parsed from string to integer before passing to engine.',
  },

  occupancyTiming: {
    key: 'occupancyTiming',
    type: 'enum',
    enumRef: 'occupancyTiming',
    notes: 'Same key name on both sides; engine uses it for heating schedule optimisation.',
  },

  temporaryGuests: {
    key: 'temporaryGuests',
    type: 'enum',
    enumRef: 'temporaryGuests',
    notes: 'Engine key is identical; used for peak DHW sizing.',
  },

  futureDemand: { key: 'futureDemand', type: 'enum' },

  // ── WATER / DHW ───────────────────────────────────────────────────────────
  hotWaterDemand: { key: 'hotWaterDemand', type: 'enum', required: true },

  usagePattern: {
    key: 'usagePattern',
    type: 'enum',
    uiOnly: true,
    notes: 'UI narrative guidance only; does not feed engine scoring.',
  },

  usageFrequency: {
    key: 'usageFrequency',
    type: 'enum',
    uiOnly: true,
    notes: 'UI narrative guidance only.',
  },

  flowRate: {
    key: 'flowRate',
    type: 'number',
    enumRef: 'flowRate',
    notes: 'UI stores as string enum (L/min); adapter parses to number before sending to engine.',
  },

  coldMainType: {
    key: 'coldMainType',
    type: 'enum',
    enumRef: 'coldMainType',
    notes: 'Same key on both sides; enum values are identical.',
  },

  // ── ENERGY / ELECTRICAL ───────────────────────────────────────────────────
  boilerType: {
    key: 'boilerType',
    type: 'enum',
    required: true,
    engineKey: 'currentBoiler',
    notes: 'Engine uses currentBoiler; values are the same boilerType enum.',
  },

  waterSystem: {
    key: 'waterSystem',
    type: 'enum',
    required: true,
    engineKey: 'currentWater',
    notes: 'Engine uses currentWater; values are the same waterSystem enum.',
  },

  electricalCapacity: {
    key: 'electricalCapacity',
    type: 'enum',
    enumRef: 'electricalCapacity',
    engineKey: 'electricalCapacityConfidence',
    engineEnumMap: {
      unknown: 'unknown',
      limited: 'insufficient',
      standard: 'likely_ok',
      upgraded: 'likely_ok',
      three_phase: 'likely_ok',
    },
    notes: 'Engine collapses five UI values to three confidence levels.',
  },

  mainsPressure: { key: 'mainsPressure', type: 'enum' },

  // ── BASELINE EFFICIENCY ───────────────────────────────────────────────────
  systemAge: {
    key: 'systemAge',
    type: 'number',
    enumRef: 'systemAge',
    engineKey: 'existingSystemAge',
    engineEnumMap: {
      '0-5': '3',
      '5-10': '7',
      '10-15': '12',
      '15-20': '17',
      '20+': '22',
      unknown: '',
    },
    notes: 'Engine expects numeric years; adapter converts band midpoint. "unknown" maps to undefined.',
  },

  efficiencySource: {
    key: 'efficiencySource',
    type: 'enum',
    enumRef: 'efficiencySource',
    engineKey: 'currentSystemEfficiency.source',
    engineEnumMap: {
      sedbuk: 'SEDBUK',
      epc: 'EPC_BAND',
      erp: 'USER',
      unknown: 'ASSUMED',
    },
    notes: 'Engine uses UPPERCASE source enum inside currentSystemEfficiency object.',
  },

  sedbukBand: {
    key: 'sedbukBand',
    type: 'enum',
    enumRef: 'sedbukBand',
    engineKey: 'currentSystemEfficiency.sedbukBand',
    notes: 'Values are A-G — identical on both sides; nested in currentSystemEfficiency.',
  },

  epcBand: {
    key: 'epcBand',
    type: 'enum',
    enumRef: 'epcBand',
    engineKey: 'currentSystemEfficiency.epcBand',
    notes: 'Values are A-G — identical on both sides; nested in currentSystemEfficiency.',
  },

  currentFuelType: {
    key: 'currentFuelType',
    type: 'enum',
    enumRef: 'currentFuelType',
    required: true,
    notes: 'Same key; engine also reads this as heatSource (inferred in converter).',
  },

  sludgeRisk: {
    key: 'sludgeRisk',
    type: 'enum',
    enumRef: 'sludgeRisk',
    engineEnumMap: {
      none: 'NONE',
      possible: 'POSSIBLE',
      likely: 'LIKELY',
      confirmed: 'CONFIRMED',
    },
    notes: 'Engine expects UPPERCASE enum values; case difference only.',
  },

  // ── EMITTERS ──────────────────────────────────────────────────────────────
  emitterType: {
    key: 'emitterType',
    type: 'enum',
    enumRef: 'emitterType',
    engineKey: 'emitters.type',
    notes: 'Canonical value placed in emitters.type nested object; same value strings.',
  },

  radiatorSize: {
    key: 'radiatorSize',
    type: 'enum',
    enumRef: 'radiatorSize',
    engineKey: 'heatEmitterType',
    engineEnumMap: {
      oversized: 'oversized_rads',
      typical: 'standard_rads',
      small: 'standard_rads',
      unknown: 'standard_rads',
    },
    notes: 'Drives heatEmitterType; oversized → oversized_rads, all others → standard_rads.',
  },

  ufhExtent: {
    key: 'ufhExtent',
    type: 'enum',
    enumRef: 'ufhExtent',
    engineKey: 'heatEmitterType',
    engineEnumMap: {
      full: 'underfloor_heating',
      partial: 'standard_rads',
      none: 'standard_rads',
    },
    notes: 'Only "full" UFH sets underfloor_heating; partial falls back to standard_rads.',
  },

  emitterLowTempSuitability: {
    key: 'emitterLowTempSuitability',
    type: 'enum',
    engineKey: 'emitters.lowTempSuitability',
    notes: 'Values are identical on both sides; nested in emitters object.',
  },

  emitterRoomsStruggle: {
    key: 'emitterRoomsStruggle',
    type: 'enum',
    enumRef: 'emitterRoomsStruggle',
    engineKey: 'emitters.roomsStruggleToHeat',
    notes: 'yes/no/unknown — same values; nested in emitters object.',
  },

  pipeBore: {
    key: 'pipeBore',
    type: 'enum',
    enumRef: 'pipeBore',
    engineKey: 'emitters.pipeBore',
    notes: '8mm/10mm/15mm/22mm/unknown — same values; nested in emitters object.',
  },

  // ── INSULATION / RETROFIT ─────────────────────────────────────────────────
  insulationLevel: {
    key: 'insulationLevel',
    type: 'enum',
    enumRef: 'insulationLevel',
    engineEnumMap: {
      good: 'high',
      average: 'medium',
      poor: 'poor',
      unknown: 'unknown',
    },
    notes: 'Engine uses high/medium/poor/unknown; UI uses good/average/poor/unknown.',
  },

  retrofitLoftInsulation: {
    key: 'retrofitLoftInsulation',
    type: 'enum',
    enumRef: 'retrofitLoft',
    engineKey: 'retrofit.loftInsulation',
    engineEnumMap: {
      none: 'none',
      some: 'some',
      good: 'good',
      unknown: 'unknown',
    },
    notes: 'Values align; explicitly mapped for clarity and adapter round-trip testing.',
  },

  retrofitWallInsulation: {
    key: 'retrofitWallInsulation',
    type: 'enum',
    enumRef: 'retrofitWall',
    engineKey: 'retrofit.wallInsulation',
    notes: 'Values are identical; nested in retrofit object.',
  },

  retrofitWindows: {
    key: 'retrofitWindows',
    type: 'enum',
    enumRef: 'retrofitWindows',
    engineKey: 'retrofit.windows',
    notes: 'Values are identical; nested in retrofit object.',
  },

  retrofitDraughtProofing: {
    key: 'retrofitDraughtProofing',
    type: 'enum',
    enumRef: 'retrofitDraught',
    engineKey: 'retrofit.draughtProofing',
    engineEnumMap: {
      none: 'none',
      partial: 'some',
      modern: 'good',
      unknown: 'unknown',
    },
    notes: 'UI uses partial/modern; engine expects some/good.',
  },

  // ── SOLAR ─────────────────────────────────────────────────────────────────
  solarIntent: {
    key: 'solarIntent',
    type: 'enum',
    enumRef: 'solarIntent',
    notes: 'Same key and same enum values on both sides.',
  },

  // ── PLANS / PREFERENCES ───────────────────────────────────────────────────
  lengthOfStay: {
    key: 'lengthOfStay',
    type: 'enum',
    required: true,
    engineKey: 'expectedLengthOfStay',
    notes: 'Engine key differs; values (short/medium/long/permanent) are identical.',
  },

  lifeStage: {
    key: 'lifeStage',
    type: 'enum',
    uiOnly: true,
    notes: 'Narrative framing only; not consumed by engine scoring.',
  },

  budgetPriority: {
    key: 'budgetPriority',
    type: 'enum',
    required: true,
    engineKey: 'costPriority',
    notes: 'Engine key is costPriority; values (upfront_cost/balanced/ongoing_cost) are identical.',
  },

  disruptionTolerance: { key: 'disruptionTolerance', type: 'enum', required: true },
  urgency: { key: 'urgency', type: 'enum', required: true },
  upgradePriorities: { key: 'upgradePriorities', type: 'array' },

  // ── THERMOSTAT ────────────────────────────────────────────────────────────
  includeThermostat: { key: 'includeThermostat', type: 'enum' },
  currentThermostatType: { key: 'currentThermostatType', type: 'enum' },
  thermostatBudget: { key: 'thermostatBudget', type: 'enum' },
  wantLoadCompensation: { key: 'wantLoadCompensation', type: 'enum' },
  wantSmartStart: { key: 'wantSmartStart', type: 'enum' },
  wantMultiZone: { key: 'wantMultiZone', type: 'enum' },

  // ── PREFERENCES (ui-only) ─────────────────────────────────────────────────
  considerCombi: {
    key: 'considerCombi',
    type: 'enum',
    uiOnly: true,
    notes: 'Controls which options are generated; not a direct engine scoring input.',
  },
  considerSystem: {
    key: 'considerSystem',
    type: 'enum',
    uiOnly: true,
    notes: 'Controls which options are generated; not a direct engine scoring input.',
  },
  considerHeatPump: {
    key: 'considerHeatPump',
    type: 'enum',
    uiOnly: true,
    notes: 'Controls which options are generated; not a direct engine scoring input.',
  },

  selectedCompareOptions: { key: 'selectedCompareOptions', type: 'array', engineKey: 'compareOptions' },
  tariffProfile: { key: 'tariffProfile', type: 'enum' },
  controlsPackagePreference: { key: 'controlsPackagePreference', type: 'enum' },
  requiredUpgradesForBestCase: { key: 'requiredUpgradesForBestCase', type: 'array' },

  // ── SECTION STATUS (ui-only) ──────────────────────────────────────────────
  sectionStatuses: {
    key: 'sectionStatuses',
    type: 'object',
    uiOnly: true,
    notes: 'UI state — tracks which sections the user has confirmed.',
  },

  // ── LCOO (passed via engine index signature) ──────────────────────────────
  enableLcoo: {
    key: 'enableLcoo',
    type: 'enum',
    uiOnly: true,
    notes: 'Controls construction of the lcoo object passed via SystemRecInput index signature.',
  },
  lcooPeriodYears: {
    key: 'lcooPeriodYears',
    type: 'number',
    uiOnly: true,
    notes: 'Nested inside the lcoo object; not a top-level SystemRecInput key.',
  },
  lcooGasTariff: {
    key: 'lcooGasTariff',
    type: 'number',
    uiOnly: true,
    notes: 'Nested inside lcoo.tariffs.gasPPerKwh.',
  },
  lcooElecTariff: {
    key: 'lcooElecTariff',
    type: 'number',
    uiOnly: true,
    notes: 'Nested inside lcoo.tariffs.elecPPerKwh.',
  },
} as const satisfies Record<string, FieldDef>;
